export type EmailDeliveryStatus =
  | 'pending'
  | 'queued'
  | 'sent'
  | 'delivered'
  | 'opened'
  | 'clicked'
  | 'bounced'
  | 'failed'
  | 'skipped';

export const EMAIL_STATUS_LABEL: Record<string, string> = {
  pending:   'Pending',
  queued:    'Queued',
  sent:      'Sent',
  delivered: 'Delivered',
  opened:    'Opened',
  clicked:   'Clicked',
  bounced:   'Bounced',
  failed:    'Failed',
  skipped:   'Skipped',
};

export function emailStatusChipClass(status: string): string {
  switch (status) {
    case 'delivered':
    case 'opened':
    case 'clicked':
      return 'ds-cp-meta-chip--success';
    case 'bounced':
    case 'failed':
      return 'ds-cp-meta-chip--danger';
    case 'sent':
    case 'queued':
    case 'pending':
      return 'ds-cp-meta-chip--warn';
    default:
      return '';
  }
}

export function formatRelativeSent(iso: string | null | undefined): string {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `about ${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
}

const EMAIL_FORMAT_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Basic RFC5322-style format check (no DNS/MX lookup). */
export function isValidEmailFormat(email: string): boolean {
  const e = email.trim();
  if (e.length < 5 || e.length > 254) return false;
  if (e.includes('..') || e.includes('@.') || e.includes('.@')) return false;
  return EMAIL_FORMAT_RE.test(e);
}

export function isGmailAddress(email: string | null | undefined): boolean {
  if (!email) return false;
  const domain = email.trim().toLowerCase().split('@')[1] ?? '';
  return domain === 'gmail.com' || domain === 'googlemail.com';
}

const SUSPICIOUS_LOCAL_RE =
  /^(test|testing|noreply|no[-_.]?reply|donotreply|invalid|fake|dummy|temp|asdf|xxx|none|null|na|n\/a|abc|sample|example)([._-]|$)/i;

/** Heuristic — blocks obvious junk; Resend may still bounce unknown domains. */
export function isLikelyDeliverableEmail(email: string): boolean {
  if (!isValidEmailFormat(email)) return false;
  const [local, domain] = email.trim().toLowerCase().split('@');
  if (!local || !domain) return false;
  if (SUSPICIOUS_LOCAL_RE.test(local)) return false;
  if (domain === 'example.com' || domain === 'test.com' || domain.endsWith('.invalid')) return false;
  return true;
}

export function outreachSendBlockReason(
  person: { email: string | null; is_reachable: boolean; outreach_state: string },
  lastStatus?: string | null,
): string | null {
  if (person.outreach_state === 'converted') return 'converted';
  if (person.outreach_state === 'do_not_contact') return 'blocked';
  if (person.outreach_state === 'bounced' || lastStatus === 'bounced') return 'bounced';
  if (!person.is_reachable) return 'not reachable';
  if (!person.email?.trim()) return 'no email';
  if (!isLikelyDeliverableEmail(person.email)) return 'invalid email format';
  if (lastStatus === 'failed') return 'previous send failed';
  return null;
}
